<script setup lang="ts">
import InternalLinks from "@/components/seo/InternalLinks.vue";
import { seoConfig } from "@/config/seo.config";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

// SEO (route-level, static & correct)
useHead({
  title: seoConfig.lailat.title,
  meta: [
    {
      name: "description",
      content: seoConfig.lailat.description,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: seoConfig.lailat.canonical,
    },
  ],
});

// i18n content
const { messages, locale } = useI18n();

const lailatPage = computed(() => {
  return messages.value[locale.value]?.pages?.lailat;
});
</script>

<template>
  <section
    v-if="lailatPage"
    class="w-full max-w-full sm:max-w-2xl mx-auto px-4 py-6 space-y-8"
  >
    <!-- Intro -->
    <header class="space-y-2">
      <h1 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
        {{ lailatPage.title }}
      </h1>
      <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
        {{ lailatPage.intro }}
      </p>
    </header>

    <!-- What -->
    <section class="space-y-2">
      <h2 class="text-base font-medium text-gray-900 dark:text-gray-100">
        {{ lailatPage.sections.what.title }}
      </h2>
      <p class="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
        {{ lailatPage.sections.what.text }}
      </p>
    </section>

    <!-- Why -->
    <section class="space-y-2">
      <h2 class="text-base font-medium text-gray-900 dark:text-gray-100">
        {{ lailatPage.sections.why.title }}
      </h2>
      <ul
        class="list-disc list-inside text-base text-gray-700 dark:text-gray-300 space-y-1"
      >
        <li v-for="(point, i) in lailatPage.sections.why.points" :key="i">
          {{ point }}
        </li>
      </ul>
    </section>

    <!-- When -->
    <section class="space-y-2">
      <h2 class="text-base font-medium text-gray-900 dark:text-gray-100">
        {{ lailatPage.sections.when.title }}
      </h2>
      <p class="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
        {{ lailatPage.sections.when.text }}
      </p>
    </section>

    <!-- Odd Nights -->
    <section class="space-y-3">
      <h2 class="text-base font-medium text-gray-900 dark:text-gray-100">
        {{ lailatPage.sections.oddNights.title }}
      </h2>

      <div class="grid grid-cols-2 gap-3">
        <div
          v-for="(night, i) in lailatPage.sections.oddNights.nights"
          :key="i"
          class="border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 text-base text-gray-800 dark:text-gray-100 text-center"
        >
          {{ night }}
        </div>
      </div>

      <p class="text-sm text-gray-500 dark:text-gray-400">
        {{ lailatPage.sections.oddNights.note }}
      </p>
    </section>

    <!-- Itikaf -->
    <section class="space-y-2">
      <h2 class="text-base font-medium text-gray-900 dark:text-gray-100">
        {{ lailatPage.sections.itikaf.title }}
      </h2>
      <p class="text-base text-gray-700 dark:text-gray-300 leading-relaxed">
        {{ lailatPage.sections.itikaf.text }}
      </p>
    </section>

    <!-- Dua -->
    <section class="space-y-3 border-t border-gray-200 pt-4">
      <h2 class="text-base font-medium text-gray-900 dark:text-gray-100">
        {{ lailatPage.sections.dua.title }}
      </h2>

      <p
        class="text-3xl text-gray-900 dark:text-gray-100 leading-relaxed font-arabic"
      >
        {{ lailatPage.sections.dua.arabic }}
      </p>

      <p class="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
        {{ lailatPage.sections.dua.meaning }}
      </p>
    </section>

    <InternalLinks current="lailat" />
  </section>
</template>
